import React, { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Box, CircularProgress, CssBaseline, TextField, Typography } from "@mui/material";
import MetaDecorator from "../services/meta_decorator";
import NDReferralHeader from "../components/shared/ndreferralheader";
import { getApiUrl } from "../services/api_service";
import Footer from "../components/shared/footer";
import { bodyBg, footerContainer, fullHeight, pcolor, rcolor, whitecolor } from "../style2";

export default function UpdateAccount() {
    const { orgCode } = useParams();
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [organizationName, setOrganizationName] = useState('');
    const [title, setTitle] = useState('');
    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [loading, setLoading] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [redirect, setRedirect] = useState('');

    useEffect(() => {
        const authTokens = localStorage.getItem("authTokens");
        let token;

        if (authTokens) {
            const parsedAuthTokens = JSON.parse(authTokens);
            token = parsedAuthTokens?.accessToken;
            setEmail(parsedAuthTokens?.email || '');
        }
        if (!token) {
            return;
        }
        setLoading(true);
        fetch(getApiUrl() + "/ReferrerAccount/istokenvalid?user=true", {
            method: "get",
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            },

        }).then(resp => {
            if (resp.status === 200) {
                resp.json().then(data => {
                    setName(data.name);
                    setTitle(data.title);
                    setOrganizationName(data.organizationName);
                    setPhone(data.phone);
                })
            } else {
                localStorage.clear();
                setRedirect(`/login/${orgCode}`);
            }
        }).catch(err => {
            setError('Unable to contact server. Check your internet connection.');
            console.log(err);
        }).finally(() => {
            setLoading(false);
        });
    }, [orgCode]);

    const updateFunc = () => {
        const authTokens = localStorage.getItem("authTokens");
        let token;

        if (authTokens) {
            const parsedAuthTokens = JSON.parse(authTokens);
            token = parsedAuthTokens?.accessToken;
        }
        if (!token) {
            return;
        }

        // Validate phone number: if starts with 0, must be 11 digits; else, must be 10 digits
        let valid = false;
        if (phone.startsWith('0')) {
            valid = /^0\d{10}$/.test(phone);
        } else {
            valid = /^\d{10}$/.test(phone);
        }
        if (!valid) {
            setPhoneError('Incomplete mobile number. Enter 11 digits if starting with 0, or 10 digits if not.');
            setLoading(false);
            return;
        }

        setError('');
        setSuccess('');
        setLoading(true);
        fetch(getApiUrl() + "/ReferrerAccount/update", {
            method: "post",
            body: JSON.stringify({ name, title, organization: organizationName, phone }),
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            }
        }).then(resp => {
            if (resp.status === 200) {
                setSuccess("Account information updated successfully");
            }
            else if (resp.status === 401 || resp.status === 403) {
                localStorage.clear();
                setRedirect(`/login/${orgCode}`);
            }
            else {
                resp.json().then(data => {
                    setError(data.error);
                    console.log(data);
                }).catch(err => {
                    setError("Unable to process the request.");
                    console.log(err);
                });
            }
        }).catch(err => {
            setError('Unable to contact server. Check your internet connection.');
            console.log(err);
        }).finally(() => {
            setLoading(false);
        });
    }

    if (redirect !== "")
        return <Navigate to={redirect} replace />
    else
        return <Box sx={{ ...bodyBg, ...fullHeight }}>
            <CssBaseline />
            <MetaDecorator title={"EnrichMyCare - Make A Referral"} description={"EnrichMycare Make Refferal"} />
            <NDReferralHeader orgCode={orgCode} logo="../../logo.jpg" />
            <Box sx={{ width: "50%", padding: "5rem 10rem", margin: "0 auto" }}>
                <form onSubmit={(e) => {
                    e.preventDefault();
                    updateFunc();
                }}>
                    <Typography component="h1" sx={{ mb: 3, color: pcolor, fontSize: "2rem", fontWeight: "bold" }}>Update Account</Typography>
                    <Box mb={3}>
                        <Typography sx={{ color: pcolor, fontSize: "1.1rem" }}>Email: {email}</Typography>
                    </Box>
                    <Box mb={3}>
                        <label style={{ color: pcolor, display: "block", fontSize: "1.1rem", marginBottom: "10px" }}>First and Last Name *</label>
                        <TextField slotProps={{ input: { maxLength: "50" } }} style={{
                            border: `1px solid ${pcolor}`, borderRadius: 5,
                            width: "100%"
                        }} variant="outlined" required={true} label="" value={name} onChange={(e) => {
                            setName(e.target.value);
                        }} />
                    </Box>
                    <Box mb={3}>
                        <label style={{ color: pcolor, display: "block", fontSize: "1.1rem", marginBottom: "10px" }}>Job Title</label>
                        <TextField slotProps={{ input: { maxLength: "50" } }} style={{
                            border: `1px solid ${pcolor}`, borderRadius: 5,
                            width: "100%"
                        }} variant="outlined" label="" value={title} onChange={(e) => {
                            setTitle(e.target.value);
                        }} />
                    </Box>
                    <Box mb={3}>
                        <label style={{ color: pcolor, display: "block", fontSize: "1.1rem", marginBottom: "10px" }}>Organisation Name (Nursery / School) *</label>
                        <TextField slotProps={{ input: { maxLength: "190" } }} style={{
                            border: `1px solid ${pcolor}`, borderRadius: 5,
                            width: "100%"
                        }} variant="outlined" required={true} label="" value={organizationName} onChange={(e) => {
                            setOrganizationName(e.target.value);
                        }} />
                    </Box>
                    <Box mb={3}>
                        <label style={{ color: pcolor, display: "block", fontSize: "1.1rem", marginBottom: "10px" }}>Mobile Number *</label>
                        <TextField
                            inputProps={{
                                maxLength: 11
                            }}
                            style={{
                                border: `1px solid ${pcolor}`,
                                borderRadius: 5,
                                width: "100%"
                            }}
                            variant="outlined"
                            label=""
                            value={phone}
                            required={true}
                            error={!!phoneError}
                            helperText={phoneError}
                            placeholder="0xxxxxxxxxx"
                            onChange={(e) => {
                                const value = e.target.value;
                                // Only allow up to 11 digits
                                if (/^\d{0,11}$/.test(value)) {
                                    setPhone(value);
                                    // Live validation: clear error if valid
                                    if ((value.startsWith('0') && value.length === 11) || (!value.startsWith('0') && value.length === 10)) {
                                        setPhoneError("");
                                    }
                                }
                            }}
                        />
                        <Typography component="div" sx={{ color: pcolor, fontSize: "0.9rem", my: 1 }}>If you provide a mobile number, you will receive the OTP on this mobile number as well.<br />
                            E.g. 07654321098</Typography>
                    </Box>
                    <Box py={3} textAlign="center">
                        <button type="submit" disabled={loading}
                            style={{
                                border: `1px solid ${rcolor}`, background: `${rcolor}`, color: `${whitecolor}`, fontSize: "1.5rem",
                                height: "50px", width: "444px", borderRadius: 2
                            }}>{loading ? <CircularProgress size={20} /> : null} Update</button>
                    </Box>
                    {error !== "" ? <Box mt={1} mb={2} sx={{ color: `${rcolor}`, fontSize: "1.1rem", textAlign: "center" }}>{error}</Box> : null}
                    {success !== "" ? <Box mt={1} mb={2} sx={{ color: "green", fontSize: "1.1rem", textAlign: "center" }}>{success}</Box> : null}
                </form>
            </Box>
            <Box sx={{ ...footerContainer, position: "static" }}>
                <Footer />
            </Box>
        </Box>;
}